import { cva, type VariantProps } from 'class-variance-authority'
import type { LucideIcon } from 'lucide-react'
import type { HTMLAttributes } from 'react'

const neoSkeuomorphismIconVariants = cva(
  [
    'relative shrink-0 shadow-[3px_5px_15px_-2px_rgb(0_0_0_/_0.075),2px_3px_6px_-4px_rgb(0_0_0_/_0.075)] dark:shadow-[3px_5px_15px_-2px_rgb(255_255_255_/_0.075),2px_3px_6px_-4px_rgb(255_255_255_/_0.075)]',
    'after:absolute after:inset-0',
  ],
  {
    variants: {
      size: {
        default: [
          'size-[60px] rounded-[20px] after:rounded-[20px] [&_svg]:size-[20px]',
          '[&_>_div:nth-child(1)]:rounded-[20px]',
          '[&_>_div:nth-child(2)]:inset-[3px] [&_>_div:nth-child(2)]:rounded-[18px]',
          '[&_>_div:nth-child(3)]:inset-[4px] [&_>_div:nth-child(3)]:rounded-[17px]',
        ],
        xl: [
          'size-[90px] rounded-[30px] after:rounded-[30px] [&_svg]:size-[30px]',
          '[&_>_div:nth-child(1)]:rounded-[30px]',
          '[&_>_div:nth-child(2)]:inset-[4px] [&_>_div:nth-child(2)]:rounded-[27px]',
          '[&_>_div:nth-child(3)]:inset-[5px] [&_>_div:nth-child(3)]:rounded-[26px]',
        ],
      },
      // NOTE: Tints don't work very well on light mode yet
      tint: {
        none: '',
        muted: 'after:bg-muted/20 after:dark:bg-muted/40 after:dark:mix-blend-exclusion',
        primary: 'after:bg-purple-500/25 after:mix-blend-color',
        success: 'after:bg-success/30 after:mix-blend-color',
        warning: 'after:bg-warning/40 after:mix-blend-color',
        destructive: 'after:bg-destructive/30 after:mix-blend-color',
      },
    },
    defaultVariants: {
      size: 'default',
      tint: 'none',
    },
  },
)

interface NeoSkeuomorphismIconProps
  extends VariantProps<typeof neoSkeuomorphismIconVariants>,
    HTMLAttributes<HTMLDivElement> {
  Icon: LucideIcon
}

export function NeoSkeuomorphismIcon({ Icon, className, size, tint }: NeoSkeuomorphismIconProps) {
  return (
    <div className={neoSkeuomorphismIconVariants({ size, tint, className })}>
      <div className="absolute inset-[0px] bg-white dark:bg-black" />
      <div className="absolute bg-gradient-to-br from-gray-200 via-white to-white dark:from-gray-700 dark:via-black dark:to-black" />
      <div className="absolute bg-gradient-to-tl from-gray-100 via-white to-white dark:from-gray-800 dark:via-black dark:to-black" />
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon className="text-foreground" />
      </div>
    </div>
  )
}
